import { AdmPlan } from "@custom-types/base-types";
import { FaroCoreApiTypes } from "@stellar/core-api-client";
import { faroCoreApiClient } from "@api/faro-core-api-client";
import { SceneSeat, SCENE_SEATS } from "@components/plans-table/scene-licenses";
import { DEFAULT_USER_ID } from "@utils/plan-utils";
import { getErrorDisplayMarkup } from "@components/error-boundary/error-boundary-utils";
import { TEAM_STELLAR_CHANNEL_URL } from "@utils/support-links";
import { getSceneSeatAssignees } from "@utils/plan-copy-utils";
import { formatPlanName } from "@utils/data-display";

/**
 * Builds the payload of the plan that grants a single SCENE seat.
 *
 * The seat is attached to `plan` and mirrors its dates, so that it is valid for exactly as long
 * as the plan it belongs to. It is created for the placeholder member with the id DEFAULT_USER_ID
 * until an admin assigns it to an actual workspace member.
 *
 * @throws When the plan has no end date, as the seat would have no end date to mirror.
 */
export function createSceneSeatPlanPayload({
  plan,
  seat,
}: {
  /** The plan the SCENE seat is attached to */
  plan: AdmPlan;

  /** The SCENE seat to create the plan for */
  seat: SceneSeat;
}): FaroCoreApiTypes.CreatePlanPayload {
  const { startDate, endDate } = plan;

  if (!endDate) {
    throw new Error(
      "SCENE seats can only be added to a plan that has an end date."
    );
  }

  return {
    subject: {
      type: "COMPANY_MEMBER",
      id: DEFAULT_USER_ID,
    },
    subscriberId: plan.subscriber.id,
    label: seat.planLabel,
    featureBundles: [],
    features: [seat.identifier],
    startDate,
    endDate,
    limits: [],
    metadata: {
      parentSubscriptionId: plan.id,
    },
  };
}

/** Converts a limit of a plan back into the payload the backend expects when updating the plan */
function toConstraintPayload({
  type,
  data,
  dates,
}: FaroCoreApiTypes.BasicLimit): FaroCoreApiTypes.PlanConstraintApiPayload {
  return {
    type,
    data: {
      max: data.max,
      roles: data.roles,
      // A limit without a grace period is a soft limit, which the backend expects as null
      gracePeriodDuration: dates.gracePeriod?.duration ?? null,
    },
  };
}

/**
 * Builds the constraints to update a plan with, so that it allows `sceneSeats` SCENE seats.
 *
 * All the other constraints of the plan are sent unchanged next to it, so that the update keeps
 * them as they are. A plan without a SCENE_SEAT constraint, i.e. one created before it existed,
 * gets one with the default grace period of the backend.
 */
export function createSceneSeatConstraintsPayload({
  plan,
  sceneSeats,
}: {
  /** The plan to change the SCENE seats of */
  plan: AdmPlan;

  /** The total number of SCENE seats the plan should allow */
  sceneSeats: number;
}): FaroCoreApiTypes.PlanConstraintApiPayload[] {
  const constraints = plan.constraints.map((constraint) => {
    const payload = toConstraintPayload(constraint);
    return constraint.type === "SCENE_SEAT"
      ? { ...payload, data: { ...payload.data, max: sceneSeats } }
      : payload;
  });

  if (constraints.some(({ type }) => type === "SCENE_SEAT")) {
    return constraints;
  }

  return [...constraints, { type: "SCENE_SEAT", data: { max: sceneSeats } }];
}

/** Outcome of creating the SCENE seats that come with a plan */
export interface SceneSeatsCreationResult {
  /** Number of SCENE seat plans that had to be created */
  requestedCount: number;

  /** Number of SCENE seat plans that could not be created */
  failedCount: number;

  /** The errors of the seats that could not be created, in the order they were requested */
  errors: unknown[];

  /** The SCENE seat plans that were created */
  createdSeats: AdmPlan[];
}

/**
 * Creates `seatCount` empty SCENE seats for every kind of SCENE seat, and attaches them to
 * the given plan.
 *
 * The seats are created independently of each other, so that a single failing request does not
 * prevent the remaining seats from being created. Callers are expected to report the failures
 * to the user, as the missing seats then have to be added manually.
 */
export async function createSceneSeatsForPlan({
  plan,
  seatCount,
}: {
  /** The plan the SCENE seats are attached to */
  plan: AdmPlan;

  /** How many seats to create for each kind of SCENE seat */
  seatCount: number;
}): Promise<SceneSeatsCreationResult> {
  // One entry for each plan that has to be created
  const seatsToCreate = SCENE_SEATS.flatMap((seat) =>
    Array.from({ length: Math.max(seatCount, 0) }, () => seat)
  );

  // Marked async so that a payload that cannot be built is reported as a failed seat,
  // instead of throwing out of here and hiding the seats that were created successfully.
  const results = await Promise.allSettled(
    seatsToCreate.map(async (seat) =>
      faroCoreApiClient.admin.createPlan(
        createSceneSeatPlanPayload({ plan, seat })
      )
    )
  );

  const errors = results
    .filter((result) => result.status === "rejected")
    .map((result) => result.reason);

  const createdSeats = results
    .filter((result) => result.status === "fulfilled")
    .map((result) => result.value);

  return {
    requestedCount: seatsToCreate.length,
    failedCount: errors.length,
    errors,
    createdSeats,
  };
}

/** Outcome of moving the SCENE seat assignments of a copied plan to the seats of its copy */
export interface SceneSeatAssignmentsTransferResult {
  /** Number of members that had a seat of the copied plan and got a seat of the copy */
  assignedCount: number;

  /** Number of members that had a seat of the copied plan, but there was no seat left for them */
  droppedCount: number;

  /** Number of requests, i.e. deactivations of old seats and assignments of new ones, that failed */
  failedCount: number;

  /** The errors of the requests that failed */
  errors: unknown[];
}

/**
 * Moves the SCENE seats of a copied plan over to its copy: the seats of the copied plan that are
 * still running are deactivated, and their members get a seat of the copy instead.
 *
 * The old seats are deactivated first, so that no member holds two seats at the same time. When
 * the copy has fewer seats than there are members holding one, the members that come last keep
 * no seat, which the admin is warned about before creating the copy.
 */
export async function transferSceneSeatAssignments({
  copiedPlan,
  oldSeats,
  newSeats,
}: {
  /** The plan that is copied */
  copiedPlan: AdmPlan;

  /** The SCENE seats of the copied plan */
  oldSeats: AdmPlan[];

  /** The SCENE seats of the copy */
  newSeats: AdmPlan[];
}): Promise<SceneSeatAssignmentsTransferResult> {
  const assignees = getSceneSeatAssignees(oldSeats);

  const seatsToDeactivate = oldSeats.filter(
    ({ status }) =>
      status.identifier === "active" || status.identifier === "invalid"
  );
  const deactivations = await Promise.allSettled(
    seatsToDeactivate.map((seat) =>
      faroCoreApiClient.admin.updatePlan({
        planId: seat.id,
        updatedPlan: {
          // eslint-disable-next-line @typescript-eslint/naming-convention
          deactivated: true,
          description: `${seat.description ?? ""}\n\nDeactivation Reason:\nThe seat moved to a copy of the plan "${formatPlanName(copiedPlan)}"`,
        },
      })
    )
  );

  const assigneesWithSeat = assignees.slice(0, newSeats.length);
  const assignments = await Promise.allSettled(
    assigneesWithSeat.map((memberIdentifier, index) =>
      faroCoreApiClient.company.assignMemberToSubscription({
        companyId: copiedPlan.subscriber.id,
        subscriptionId: newSeats[index].id,
        payload: { memberIdentifier },
      })
    )
  );

  const errors = [...deactivations, ...assignments]
    .filter((result) => result.status === "rejected")
    .map((result) => result.reason);
  const failedAssignments = assignments.filter(
    (result) => result.status === "rejected"
  ).length;

  return {
    assignedCount: assigneesWithSeat.length - failedAssignments,
    droppedCount: assignees.length - assigneesWithSeat.length,
    failedCount: errors.length,
    errors,
  };
}

/** Title of the error that is shown when not all SCENE seats of a copied plan could be moved */
export const SCENE_SEATS_TRANSFER_FAILED_TITLE =
  "Not all SCENE seat assignments could be copied";

/**
 * Builds the body of the error that is shown when not all SCENE seats of a copied plan could be
 * moved to its copy. Both plans exist at that point, so the seats have to be fixed manually.
 */
export function sceneSeatsTransferFailedMarkup({
  failedCount,
  errors,
}: SceneSeatAssignmentsTransferResult): string {
  const [firstError] = errors;

  return [
    `<p>${failedCount} of the SCENE seats of the copied plan could not be deactivated or assigned to their members in the copy.</p>`,
    `<p>Please check the SCENE seats of both plans, and report this in the <a href="${TEAM_STELLAR_CHANNEL_URL}" rel="noopener noreferrer">Team Stellar Teams channel</a> if they cannot be fixed manually.</p>`,
    firstError === undefined
      ? ""
      : `<p>${getErrorDisplayMarkup(firstError)}</p>`,
  ].join("");
}

/** Title of the error that is shown when not all SCENE seats of a plan could be created */
export const SCENE_SEATS_CREATION_FAILED_TITLE =
  "Not all SCENE seats could be created";

/**
 * Builds the body of the error that is shown when not all SCENE seats of a plan could be created.
 *
 * The plan itself already exists at that point, so the missing seats have to be added manually.
 * The result is rendered as sanitized HTML by the error dialog, which is what allows it to link
 * to the Teams channel the admin should report the problem in. The sanitizer drops `target`, so
 * the link always opens in the same tab.
 */
export function sceneSeatsCreationFailedMarkup({
  requestedCount,
  failedCount,
  errors,
}: Omit<SceneSeatsCreationResult, "createdSeats">): string {
  const [firstError] = errors;

  return [
    `<p>${failedCount} of ${requestedCount} SCENE seats could not be created for this plan.</p>`,
    `<p>Please report this immediately in the <a href="${TEAM_STELLAR_CHANNEL_URL}" rel="noopener noreferrer">Team Stellar Teams channel</a>, so that the missing seats can be added manually.</p>`,
    firstError === undefined
      ? ""
      : `<p>${getErrorDisplayMarkup(firstError)}</p>`,
  ].join("");
}
